import * as THREE from 'three';
import type { SoftBarrierHit, WallHit } from './Collision';

export const NO_HIT: WallHit = { hit: false, impactSpeed: 0, nx: 0, nz: 0 };
export const NO_SOFT_HIT: SoftBarrierHit = { ...NO_HIT, scraping: false };

export function pickDominantHit(...hits: Array<WallHit | null | undefined>): WallHit {
  let dominant: WallHit = NO_HIT;
  for (const h of hits) {
    if (h?.hit && h.impactSpeed > dominant.impactSpeed) dominant = h;
  }
  return dominant;
}

export function wallSlideSpeed(hit: WallHit, velocity: THREE.Vector3): number {
  if (!hit.hit) return 0;
  const normalVelocity = velocity.x * hit.nx + velocity.z * hit.nz;
  const tangentX = velocity.x - normalVelocity * hit.nx;
  const tangentZ = velocity.z - normalVelocity * hit.nz;
  return Math.hypot(tangentX, tangentZ);
}

export function pickWallContact(velocity: THREE.Vector3, ...hits: Array<WallHit | null | undefined>): WallHit {
  let best: WallHit = NO_HIT;
  let bestScore = 0;
  for (const hit of hits) {
    if (!hit?.hit) continue;
    const score = hit.impactSpeed + wallSlideSpeed(hit, velocity) * 0.35;
    if (score > bestScore) {
      best = hit;
      bestScore = score;
    }
  }
  return best;
}

export function sameWallNormal(a: { nx: number; nz: number }, b: WallHit, minDot: number): boolean {
  if (!b.hit) return false;
  const aLen = Math.hypot(a.nx, a.nz);
  const bLen = Math.hypot(b.nx, b.nz);
  if (aLen <= 1e-6 || bLen <= 1e-6) return false;
  const dot = (a.nx * b.nx + a.nz * b.nz) / (aLen * bLen);
  return dot >= THREE.MathUtils.clamp(minDot, -1, 1);
}

export function wallContactSource(
  hit: WallHit,
  refs: {
    corridorHit: WallHit;
    scrapeHit: WallHit;
    obstacleHit: WallHit;
    wallObstacleHit: WallHit;
    meshHit: WallHit;
    terrainHit: WallHit;
  },
): string {
  if (!hit.hit) return 'none';
  if (hit === refs.corridorHit) return 'corridor';
  if (hit === refs.scrapeHit) return 'rail';
  if (hit === refs.wallObstacleHit) return 'wall obb';
  if (hit === refs.obstacleHit) return 'scenery obb';
  if (hit === refs.meshHit) return 'mesh fallback';
  if (hit === refs.terrainHit) return 'terrain';
  return 'contact';
}
